import { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { getTeamUsers, updateUser, getValidators } from '../lib/api';
import type { TeamUser, AppName, AppRole } from '../types';
import { computeTrustLevel, DEFAULT_APP_ROLES } from '../types';
import styles from './AdminPage.module.css';

// ─── Constants ────────────────────────────────────────────────────────────────

const APPS: { value: AppName; label: string; color: string }[] = [
  { value: 'broota',      label: 'Broota',      color: 'var(--broota)'  },
  { value: 'xbroota',     label: 'Xbroota',     color: 'var(--xbroota)' },
  { value: 'nalca',       label: 'Nalca',       color: 'var(--nalca)'   },
  { value: 'dully',       label: 'Dully',       color: 'var(--dully)'   },
  { value: 'team-ticket', label: 'Oskiapp',    color: 'var(--accent)'  },
];

const ROLE_CYCLE: AppRole[] = ['none', 'viewer', 'junior', 'dev', 'senior'];

const ROLE_META: Record<AppRole, { label: string; short: string; color: string }> = {
  none:   { label: 'Sin acceso',    short: '—',   color: 'var(--muted2)'   },
  viewer: { label: 'Solo lectura',  short: 'VER', color: 'var(--muted)'    },
  junior: { label: 'Junior',        short: 'JR',  color: 'var(--dully)'    },
  dev:    { label: 'Desarrollador', short: 'DEV', color: 'var(--xbroota)'  },
  senior: { label: 'Senior',        short: 'SR',  color: 'var(--nalca)'    },
};

function getInitials(name: string) {
  return name.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase();
}

function ensureAppRoles(u: TeamUser): Record<AppName, AppRole> {
  return { ...DEFAULT_APP_ROLES, ...(u.appRoles ?? {}) };
}

type Validator = Pick<TeamUser, 'uid' | 'displayName'>;

// ─── RoleCell ─────────────────────────────────────────────────────────────────
// Cycling button: tap to advance none → junior → senior → none

function RoleCell({
  role, appColor, isSysadmin, saving,
  onChange,
}: {
  role:       AppRole;
  appColor:   string;
  isSysadmin: boolean;
  saving:     boolean;
  onChange:   (r: AppRole) => void;
}) {
  // Migración: 'external' fue eliminado — tratarlo como 'none'
  const safeRole: AppRole = (role in ROLE_META) ? role : 'none';
  const meta = ROLE_META[safeRole];

  if (isSysadmin) {
    return (
      <div className={styles.cellSys} title="Sysadmin — acceso total">
        <span style={{ color: 'var(--broota)', fontWeight: 600, fontSize: 10 }}>SYS</span>
      </div>
    );
  }

  function cycle() {
    const idx = ROLE_CYCLE.indexOf(safeRole);
    const nextRole = ROLE_CYCLE[(idx + 1) % ROLE_CYCLE.length];
    // Pedir confirmación al bajar de un rol activo a 'none'
    if (safeRole !== 'none' && nextRole === 'none') {
      if (!window.confirm(`¿Eliminar acceso "${ROLE_META[safeRole].label}"? Esta acción revoca el permiso del usuario en esta app.`)) return;
    }
    onChange(nextRole);
  }

  return (
    <button
      className={`${styles.roleCell} ${safeRole !== 'none' ? styles.roleCellActive : ''}`}
      style={safeRole !== 'none' ? { '--role-color': meta.color } as React.CSSProperties : undefined}
      onClick={cycle}
      disabled={saving}
      title={`${meta.label} — click para cambiar`}
      aria-label={`Rol: ${meta.label}. Click para cambiar.`}
    >
      {safeRole === 'none'
        ? <span className={styles.roleDash}>—</span>
        : <span className={styles.roleShort}>{meta.short}</span>
      }
    </button>
  );
}

// ─── UserRow ──────────────────────────────────────────────────────────────────

function UserRow({
  user, validators, expanded,
  onToggle, onRoleChange, onSysadminChange, onValidatorChange,
}: {
  user:              TeamUser;
  validators:        Validator[];
  expanded:          boolean;
  onToggle:          () => void;
  onRoleChange:      (app: AppName, role: AppRole) => void;
  onSysadminChange:  (v: boolean) => void;
  onValidatorChange: (uid: string, name: string) => void;
}) {
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(0);
  const [saveError, setSaveError] = useState<string | null>(null);
  const appRoles = ensureAppRoles(user);

  const justSaved = Date.now() - savedAt < 2000;

  async function handleRoleChange(app: AppName, role: AppRole) {
    onRoleChange(app, role);
    setSaving(true);
    setSaveError(null);
    const newAppRoles = { ...appRoles, [app]: role };
    const newTrustLevel = computeTrustLevel(user.isSysadmin, newAppRoles);
    try {
      await updateUser(user.uid, { appRoles: newAppRoles, trustLevel: newTrustLevel });
      setSavedAt(Date.now());
    } catch (err) {
      setSaveError('Error al guardar rol');
      onRoleChange(app, appRoles[app]); // rollback
    } finally {
      setSaving(false);
    }
  }

  async function handleSysadmin(v: boolean) {
    onSysadminChange(v);
    setSaving(true);
    setSaveError(null);
    const newTrustLevel = computeTrustLevel(v, appRoles);
    try {
      await updateUser(user.uid, { isSysadmin: v, trustLevel: newTrustLevel });
      setSavedAt(Date.now());
    } catch (err) {
      setSaveError('Error al guardar admin');
      onSysadminChange(!v); // rollback
    } finally {
      setSaving(false);
    }
  }

  async function handleValidator(validatorUid: string) {
    const val = validators.find(v => v.uid === validatorUid);
    onValidatorChange(validatorUid, val?.displayName ?? '');
    setSaving(true);
    try {
      await updateUser(user.uid, {
        validatorUid:  validatorUid || undefined,
        validatorName: val?.displayName,
      });
      setSavedAt(Date.now());
    } finally {
      setSaving(false);
    }
  }

  const needsValidator = !user.isSysadmin &&
    Object.values(appRoles).some(r => r === 'junior');

  // Avatar color = highest role
  const avatarColor = user.isSysadmin ? 'var(--broota)'
    : Object.values(appRoles).includes('senior') ? 'var(--nalca)'
    : Object.values(appRoles).includes('dev')    ? 'var(--xbroota)'
    : Object.values(appRoles).includes('junior') ? 'var(--dully)'
    : Object.values(appRoles).includes('viewer') ? 'var(--muted)'
    : 'var(--surface3)';

  return (
    <>
      <tr
        className={`${styles.row} ${expanded ? styles.rowExpanded : ''}`}
        onClick={onToggle}
        aria-expanded={expanded}
        tabIndex={0}
        onKeyDown={e => e.key === 'Enter' && onToggle()}
      >
        {/* User info */}
        <td className={styles.cellUser}>
          <div className={styles.userAvatar} style={{ background: avatarColor }}>
            {getInitials(user.displayName)}
          </div>
          <div className={styles.userInfo}>
            <div className={styles.userName}>{user.displayName}</div>
            <div className={styles.userEmail}>{user.email}</div>
          </div>
          {saving    && <span className={styles.savingDot} title="Guardando..." />}
          {justSaved && !saving && !saveError && <span className={styles.savedDot} title="Guardado" />}
          {saveError && !saving && <span className={styles.errorDot} title={saveError}>!</span>}
        </td>

        {/* App role cells */}
        {APPS.map(app => (
          <td key={app.value} className={styles.cellRole}
            onClick={e => e.stopPropagation()}>
            <RoleCell
              role={appRoles[app.value]}
              appColor={app.color}
              isSysadmin={user.isSysadmin}
              saving={saving}
              onChange={role => handleRoleChange(app.value, role)}
            />
          </td>
        ))}

        {/* Sysadmin toggle */}
        <td className={styles.cellAdmin} onClick={e => e.stopPropagation()}>
          <button
            className={`${styles.sysToggle} ${user.isSysadmin ? styles.sysToggleOn : styles.sysToggleOff}`}
            onClick={() => handleSysadmin(!user.isSysadmin)}
            disabled={saving}
            title={user.isSysadmin ? 'Quitar acceso admin' : 'Dar acceso admin (Sysadmin)'}
            aria-pressed={user.isSysadmin}
          >
            {user.isSysadmin ? 'SYS ✓' : '+ SYS'}
          </button>
        </td>
      </tr>

      {/* Expanded detail row */}
      {expanded && (
        <tr className={styles.detailRow}>
          <td colSpan={6} className={styles.detailCell}>
            <div className={styles.detail}>
              {needsValidator && (
                <div className={styles.detailField}>
                  <label className={styles.detailLabel}>Validador asignado</label>
                  <select
                    className={styles.detailSelect}
                    value={user.validatorUid ?? ''}
                    onChange={e => handleValidator(e.target.value)}
                  >
                    <option value="">— Sin validador —</option>
                    {validators
                      .filter(v => v.uid !== user.uid)
                      .map(v => (
                        <option key={v.uid} value={v.uid}>{v.displayName}</option>
                      ))}
                  </select>
                  <span className={styles.detailHint}>
                    Requerido para usuarios con rol Junior o Externo
                  </span>
                </div>
              )}
              {!needsValidator && (
                <span className={styles.detailHint}>
                  {user.isSysadmin
                    ? 'Sysadmin — acceso completo al sistema y al panel de admin'
                    : 'Asigná al menos un rol Junior o Externo para configurar un validador'}
                </span>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// ─── Legend ───────────────────────────────────────────────────────────────────

function Legend() {
  return (
    <div className={styles.legend}>
      {(['none', 'viewer', 'junior', 'dev', 'senior'] as AppRole[]).map(role => {
        const m = ROLE_META[role];
        return (
          <span key={role} className={styles.legendItem}>
            <span className={styles.legendBadge}
              style={{ color: m.color, borderColor: `color-mix(in srgb, ${m.color} 35%, transparent)` }}>
              {m.short}
            </span>
            {m.label}
          </span>
        );
      })}
      <span className={styles.legendSep} />
      <span className={styles.legendHint}>Click en una celda para cambiar el rol</span>
    </div>
  );
}

// ─── AdminPage ────────────────────────────────────────────────────────────────

export function AdminPage() {
  const { isSysadmin } = useAuth();

  const [users,      setUsers]      = useState<TeamUser[]>([]);
  const [validators, setValidators] = useState<Validator[]>([]);
  const [expandedUid, setExpandedUid] = useState<string | null>(null);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState<string | null>(null);

  if (!isSysadmin) return <Navigate to="/" replace />;

  useEffect(() => {
    Promise.all([getTeamUsers(), getValidators()])
      .then(([fetchedUsers, vals]) => {
        setUsers(fetchedUsers);
        setValidators(vals);
      })
      .catch(() => setError('No se pudieron cargar los usuarios.'))
      .finally(() => setLoading(false));
  }, []);

  function updateUserField(uid: string, patch: Partial<TeamUser>) {
    setUsers(prev => prev.map(u => u.uid === uid ? { ...u, ...patch } : u));
  }

  function handleRoleChange(uid: string, app: AppName, role: AppRole) {
    const user = users.find(u => u.uid === uid);
    if (!user) return;
    const newAppRoles = { ...ensureAppRoles(user), [app]: role };
    const newTrustLevel = computeTrustLevel(user.isSysadmin, newAppRoles);
    updateUserField(uid, { appRoles: newAppRoles, trustLevel: newTrustLevel });
  }

  function handleSysadminChange(uid: string, v: boolean) {
    const user = users.find(u => u.uid === uid);
    if (!user) return;
    const newTrustLevel = computeTrustLevel(v, ensureAppRoles(user));
    updateUserField(uid, { isSysadmin: v, trustLevel: newTrustLevel });
  }

  function handleValidatorChange(uid: string, validatorUid: string, validatorName: string) {
    updateUserField(uid, { validatorUid: validatorUid || undefined, validatorName });
  }

  return (
    <div className={styles.page}>

      {/* Header */}
      <div className={styles.pageHeader}>
        <div>
          <div className={styles.pageTitle}>Equipo</div>
          <div className={styles.pageSub}>Rol por app · click en una celda para editar · guarda automático</div>
        </div>
        {!loading && (
          <span className={styles.countBadge}>{users.length} miembros</span>
        )}
      </div>

      {error && (
        <div className={styles.errorBanner} role="alert">{error}</div>
      )}

      {/* Matrix table */}
      <div className={styles.tableWrap}>
        {loading ? (
          <div className={styles.loadingState}>Cargando equipo...</div>
        ) : (
          <table className={styles.matrix}>
            <thead>
              <tr>
                <th className={styles.thUser}>Usuario</th>
                {APPS.map(app => (
                  <th key={app.value} className={styles.thApp}>
                    <span className={styles.appDot} style={{ background: app.color }} />
                    {app.label}
                  </th>
                ))}
                <th className={styles.thAdmin}>Admin</th>
              </tr>
            </thead>
            <tbody>
              {users.map(user => (
                <UserRow
                  key={user.uid}
                  user={user}
                  validators={validators}
                  expanded={expandedUid === user.uid}
                  onToggle={() => setExpandedUid(prev => prev === user.uid ? null : user.uid)}
                  onRoleChange={(app, role) => handleRoleChange(user.uid, app, role)}
                  onSysadminChange={v => handleSysadminChange(user.uid, v)}
                  onValidatorChange={(uid, name) => handleValidatorChange(user.uid, uid, name)}
                />
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Legend />
    </div>
  );
}
